import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, MicOff, Volume2, HelpCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  isSpeechRecognitionSupported,
  getVoiceEngine,
  getVoiceEnabled,
  setVoiceEnabled,
  VOICE_COMMANDS,
  type VoiceEvent,
  type VoiceCommand,
} from '../../utils/voiceCommands';
import { announce } from '../../utils/announce';
import { hapticTap, hapticSuccess } from '../../utils/haptics';

/**
 * Voice Control Hook
 * 
 * Provides voice command functionality to any component.
 */
export const useVoiceControl = () => {
  const navigate = useNavigate();
  const [isListening, setIsListening] = useState(false);
  const [isSupported] = useState(isSpeechRecognitionSupported);
  const [lastTranscript, setLastTranscript] = useState('');
  const [lastCommand, setLastCommand] = useState<VoiceCommand | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHelp, setShowHelp] = useState(false);

  // Execute a command
  const executeCommand = useCallback((command: VoiceCommand, transcript: string) => {
    hapticSuccess();
    setLastCommand(command);
    setLastTranscript(transcript);
    
    const [type, value] = command.action.split(':');
    
    switch (type) {
      case 'navigate':
        announce(`Navigating to ${command.description}`);
        navigate(value);
        break;
        
      case 'action':
        switch (value) {
          case 'back':
            announce('Going back');
            navigate(-1);
            break;
          case 'scrollDown':
            window.scrollBy({ top: 300, behavior: 'smooth' });
            announce('Scrolling down');
            break;
          case 'scrollUp':
            window.scrollBy({ top: -300, behavior: 'smooth' });
            announce('Scrolling up');
            break;
          case 'checkIn':
            // Dispatch custom event for reminder check-in
            window.dispatchEvent(new CustomEvent('voiceCheckIn'));
            announce('Checking in');
            break;
          case 'emergency':
            window.location.href = 'tel:911';
            announce('Calling emergency services');
            break;
          case 'refresh':
            window.location.reload();
            break;
        }
        break;
        
      case 'system':
        switch (value) {
          case 'stopListening':
            stopListening();
            announce('Voice control stopped');
            break;
          case 'help':
            setShowHelp(true);
            announce('Showing voice command help');
            break;
        }
        break;
    }
  }, [navigate]);

  // Handle voice events
  useEffect(() => {
    if (!isSupported) return;

    const engine = getVoiceEngine();
    
    const unsubscribe = engine.subscribe((event: VoiceEvent) => {
      switch (event.type) {
        case 'start':
          setIsListening(true);
          setError(null);
          break;
          
        case 'end':
          setIsListening(false);
          break;
          
        case 'command':
          if (event.command && event.transcript) {
            executeCommand(event.command, event.transcript);
          }
          break;
          
        case 'noMatch':
          if (event.transcript) {
            setLastTranscript(event.transcript);
            announce(`I didn't understand "${event.transcript}". Say "help" for commands.`);
          }
          break;
          
        case 'error':
          setError(event.error || 'Unknown error');
          if (event.error === 'not-allowed') {
            announce('Microphone access denied. Please enable it in your browser settings.');
          }
          break;
      }
    });

    return unsubscribe;
  }, [isSupported, executeCommand]);

  const startListening = useCallback(() => {
    if (!isSupported) return;
    hapticTap();
    setVoiceEnabled(true);
    getVoiceEngine().start();
    announce('Listening for voice commands');
  }, [isSupported]);

  const stopListening = useCallback(() => {
    if (!isSupported) return;
    hapticTap();
    setVoiceEnabled(false);
    getVoiceEngine().stop();
    announce('Voice control stopped');
  }, [isSupported]);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  return {
    isSupported,
    isListening,
    lastTranscript,
    lastCommand,
    error,
    showHelp,
    setShowHelp,
    startListening,
    stopListening,
    toggleListening,
    commands: VOICE_COMMANDS,
  };
};

/**
 * Voice Control Button Component
 * 
 * A floating button that toggles voice control.
 */
interface VoiceControlButtonProps {
  className?: string;
}

export const VoiceControlButton: React.FC<VoiceControlButtonProps> = ({ className = '' }) => {
  const {
    isSupported,
    isListening,
    lastTranscript,
    showHelp,
    setShowHelp,
    toggleListening,
    commands,
  } = useVoiceControl();

  if (!isSupported) return null;

  return (
    <>
      {/* Floating Voice Button */}
      <motion.button
        onClick={toggleListening}
        className={`fixed bottom-24 right-4 z-40 p-4 rounded-full shadow-lg transition-all ${
          isListening 
            ? 'bg-red-500 text-white animate-pulse' 
            : 'bg-primary text-white hover:bg-primary-dark'
        } ${className}`}
        whileTap={{ scale: 0.95 }}
        aria-label={isListening ? 'Stop listening' : 'Start voice control'}
        title={isListening ? 'Tap to stop' : 'Tap for voice control'}
      >
        {isListening ? <MicOff size={24} /> : <Mic size={24} />}
      </motion.button>

      {/* Listening Indicator - Enhanced Global Banner */}
      <AnimatePresence>
        {isListening && (
          <>
            {/* Top Banner */}
            <motion.div
              initial={{ opacity: 0, y: -50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -50 }}
              className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-red-500 via-red-600 to-red-500 text-white py-3 px-4 shadow-lg"
            >
              <div className="flex items-center justify-center gap-3 max-w-md mx-auto">
                {/* Animated sound wave bars */}
                <div className="flex items-center gap-1">
                  <motion.div
                    className="w-1 bg-white rounded-full"
                    animate={{ height: [8, 20, 8] }}
                    transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
                  />
                  <motion.div
                    className="w-1 bg-white rounded-full"
                    animate={{ height: [12, 6, 12] }}
                    transition={{ duration: 0.4, repeat: Infinity, ease: "easeInOut", delay: 0.1 }}
                  />
                  <motion.div
                    className="w-1 bg-white rounded-full"
                    animate={{ height: [6, 16, 6] }}
                    transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                  />
                  <motion.div
                    className="w-1 bg-white rounded-full"
                    animate={{ height: [14, 8, 14] }}
                    transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut", delay: 0.15 }}
                  />
                </div>
                
                <span className="font-bold text-lg">Listening...</span>
                
                {/* Pulsing mic icon */}
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  <Mic size={20} />
                </motion.div>
              </div>
              
              {lastTranscript && (
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center text-sm text-white/80 mt-1 truncate max-w-xs mx-auto"
                >
                  "{lastTranscript}"
                </motion.p>
              )}
            </motion.div>
            
            {/* Floating indicator near button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="fixed bottom-40 right-4 z-40 bg-surface rounded-2xl shadow-lg p-3 max-w-[200px]"
            >
              <div className="flex items-center gap-2 text-sm">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-text-primary font-medium">Active</span>
              </div>
              <button
                onClick={() => setShowHelp(true)}
                className="text-xs text-primary mt-2 flex items-center gap-1"
              >
                <HelpCircle size={12} />
                Voice commands
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowHelp(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-surface rounded-3xl p-6 w-full max-w-sm max-h-[80vh] overflow-auto shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-primary flex items-center gap-2">
                  <Volume2 size={20} />
                  Voice Commands
                </h2>
                <button
                  onClick={() => setShowHelp(false)}
                  className="p-2 hover:bg-background rounded-full"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Navigation Commands */}
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary mb-2">
                    Navigation
                  </h3>
                  <div className="space-y-1">
                    {commands.filter(c => c.type === 'navigation').map((cmd, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-text-muted">"{cmd.patterns[0]}"</span>
                        <span className="text-text-primary">{cmd.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Commands */}
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary mb-2">
                    Actions
                  </h3>
                  <div className="space-y-1">
                    {commands.filter(c => c.type === 'action').map((cmd, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-text-muted">"{cmd.patterns[0]}"</span>
                        <span className="text-text-primary">{cmd.description}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* System Commands */}
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary mb-2">
                    System
                  </h3>
                  <div className="space-y-1">
                    {commands.filter(c => c.type === 'system').map((cmd, i) => (
                      <div key={i} className="flex justify-between text-sm">
                        <span className="text-text-muted">"{cmd.patterns[0]}"</span>
                        <span className="text-text-primary">{cmd.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <p className="text-xs text-text-muted mt-4 text-center">
                Tap the microphone button and speak clearly
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default VoiceControlButton;
